import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { FLEX_CONTAINER, FLEX_CONTAINER_COLUMN, FLEX_ITEM_AUTO } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { createHtmlElement, empty } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { DEFAULT_DATETIME_FORMAT, format, toDate, TODAY, UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools";
import { UID } from "@cervval.js/common/src/tools/type.tools";
import { ColumnUid } from "../dayAgenda/dayAgenda.types";

export const DAY_AGENDA_HEADER_TICK_CLASS = "day-agenda-header-tick";
export const DAY_AGENDA_HEADER_TICK_DAY_CLASS = "day-agenda-header-tick-day";
export const DAY_AGENDA_HEADER_TICK_DAY_NUMBER_CLASS = "day-agenda-header-tick-day-number";

/** Information on a agendaTimescale step. Used to draw agendaTimescale ticks. */
export interface DayAgendaHeaderTickInfo {
    /** Tick's TsDate */
    date: UnixTimestamp;

    /** Column uid */
    columnUid: ColumnUid;
}


/** Store information about timescaleTick */
export interface DayAgendaHeaderTick {

    /** Html content of tick */
    html: HTMLDivElement;

    /** Instant of tick (start of tick) */
    instant: UnixTimestamp;
}

export interface DayAgendaHeaderContext {
    /** Map of tick infos foreach given column */
    headerColumnsMap: Map<ColumnUid, DayAgendaHeaderTickInfo>;
}

/** Define all available options of agendaTimescale */
export interface DayAgendaHeaderOptions {
    /** Setting agendaTimescale uid affects the AgendaHeaderUnitsOptions computation */
    uid?: UID;
}

@CustomElement({
    name: "cjs-day-agenda-header",
    style: require("./dayAgendaHeader.scss"),
    template: require("./dayAgendaHeader.html"),
    elementCssClasses: [FLEX_CONTAINER]
})
export class CjsDayAgendaHeader extends WebComponent {

    @Injected(LOG_SERVICE, { optional: true })
    protected readonly _log?: LogService;

    /** Timescale context */
    private _context: DayAgendaHeaderContext | undefined;

    @Event(NoBubblesEventInit)
    public itemClickEvent!: EventHandler<{ dayClicked: UnixTimestamp, columnUid: ColumnUid }>;

    /** Current ticks of agendaTimescale */
    public ticks: DayAgendaHeaderTick[] = [];

    /** Define if the unit options are forced in the initialize. Won't update it on refresh */
    protected _agendaTickInfos: DayAgendaHeaderTickInfo[] = [];

    /**
     * setting agendaTimescale uid affects the AgendaHeaderUnitsOptions computation
     */
    protected _uid?: UID;

    /**
     * The initialization of the smartSelect
     */
    constructor(context?: DayAgendaHeaderContext, options?: Partial<DayAgendaHeaderOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this._uid = options?.uid;
        this._context = context ? { ...context } : undefined;
    }

    /**
     * init component
     */
    public override init(): void {

    }

    /**
     * Refresh agendaTimescale and its context if specified. Compute new agendaTimescale infos and rebuild ticks
     * @param reason refresh reason
     * @param context AgendaHeader context
     */
    public override refresh(reason?: string, context?: DayAgendaHeaderContext): void {
        // Reset ticks
        this.ticks = [];

        if (context) {
            this._context = { ...context };// use provided context
        }

        if (this._context) {
            this._agendaTickInfos = this._computeHeaderTickInfos(this._context);
            rebuildHeaderTicks(this, this._agendaTickInfos, this);
        }
    }

    /** Compute header tick infos */
    protected _computeHeaderTickInfos(context: DayAgendaHeaderContext): DayAgendaHeaderTickInfo[] {
        return Array.from(context.headerColumnsMap.values() ?? []);
    }

    /** Returns agendaTimescale's infos (time ticks) */
    public getAgendaHeaderTickInfos(): DayAgendaHeaderTickInfo[] {
        return this._agendaTickInfos;
    }
}


/**
 * Rebuild agendaTimescale's ticks from agendaTimescale's infos
 * @param scaleDiv agendaTimescale's div
 */
export function rebuildHeaderTicks(scaleDiv: HTMLElement, agendaHeaderTickInfo: DayAgendaHeaderTickInfo[], agendaHeader?: CjsDayAgendaHeader): void {

    empty(scaleDiv);

    const divTicks: HTMLElement[] = [];
    for (const info of agendaHeaderTickInfo) {
        // we only build the first encountered tick
        const divTick = _buildOneTickOnAgendaHeader(info);
        divTicks.push(divTick.tick);

        if (agendaHeader) {
            agendaHeader.ticks.push({
                html: divTick.tick,
                instant: info.date
            });

            divTick.dayNumberDiv.addEventListener("click", () => {
                agendaHeader.itemClickEvent.emit({ dayClicked: info.date, columnUid: info.columnUid });
            });
        }
    }

    // Append tick only one time, avoid multiple forced style computation
    scaleDiv.append(...divTicks);
}

/**
 * Build a div agendaTimescale tick from step info and relative to its container location.
 * @param agendaTimescaleInfo AgendaHeader step info to draw tick
 * @returns tick's div
 */
function _buildOneTickOnAgendaHeader(agendaTimescaleInfo: DayAgendaHeaderTickInfo): { tick: HTMLDivElement, dayNumberDiv: HTMLDivElement } {
    const dateTs = toDate(agendaTimescaleInfo.date);

    const isToday = dateTs.uts === TODAY();
    const dayNumberDiv = createHtmlElement("div", {
        cssClass: [DAY_AGENDA_HEADER_TICK_DAY_NUMBER_CLASS, isToday ? "today" : ""],
        innerHTML: String(dateTs.day)
    });

    const dayStr = format(agendaTimescaleInfo.date, "ccc");
    const div = createHtmlElement("div", {
        cssClass: [DAY_AGENDA_HEADER_TICK_CLASS, FLEX_ITEM_AUTO, FLEX_CONTAINER_COLUMN],
        attributes: {
            day: format(dateTs, DEFAULT_DATETIME_FORMAT),
            instant: String(agendaTimescaleInfo.date)
        },
        innerHTML: [
            createHtmlElement("div", {
                cssClass: [DAY_AGENDA_HEADER_TICK_DAY_CLASS],
                innerHTML: dayStr
            }),
            dayNumberDiv
        ]
    });


    return { tick: div, dayNumberDiv };
}